import { Ref } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { FactCheckOutlined } from 'components/icons';
import { Button } from 'components/ui/button';
import { cn } from 'utils/cn';
import { countOpen } from './commentTree';
import { useInvoiceComments } from './useInvoiceComments';

type Props = {
    invoiceId: number;
    expanded: boolean;
    onOpen: () => void;
    buttonRef: Ref<HTMLButtonElement>;
    /** Merged onto the button's own classes. */
    className?: string;
    /** The glyph before the label; the new design's band draws its lucide clipboard. */
    icon?: typeof FactCheckOutlined;
};

/** The invoice page's Comments button: blue with the count while comments are open, neutral without. */
export function InvoiceCommentsButton({
    invoiceId,
    expanded,
    onOpen,
    buttonRef,
    className,
    icon: Icon = FactCheckOutlined,
}: Props) {
    const intl = useIntl();
    const { data } = useInvoiceComments(invoiceId);
    const open = countOpen(data?.comments);
    const ink = open > 0 && 'new:text-info text-blue-700';
    return (
        <Button
            ref={buttonRef}
            {...buttonStyle({ variant: 'outlined', color: 'secondary' }, cn('gap-1', className))}
            aria-label={intl.formatMessage({ id: 'invoice-comments.button-label' }, { count: open })}
            aria-expanded={expanded}
            onClick={onOpen}
        >
            <Icon className={cn(ink)} />
            <span className={cn(ink)}>
                <FormattedMessage id="invoice-comments.button" />
            </span>
            {open > 0 ? <span className={cn('text-xs leading-5 font-semibold', ink)}>{open}</span> : null}
        </Button>
    );
}
